import { Banner, Button, Dialog, Typography } from '@neo4j-ndl/react';
import { CopyTextInput } from '@nx/ui';
import type { InstanceCredentials } from '@nx/ui/src/utils';
import { formatInstanceCredentials } from '@nx/ui/src/utils';
import React, { useMemo } from 'react';

interface Props {
  id: string;
  password: string;
  boltUrl: string;
  name: string;
  username: string;
  databaseName?: string;
  onAccept: () => void;
  onDecline: () => void;
  liveMigration: boolean;
}
/*
This file is very similar to CreateInstanceModal but differs in the way its used and slotted into the dialog structure.
If there is a way to fully use the original please change it, otherwise we have to live with both.
 */
export const CredentialsMigrationDialogStep = ({
  id,
  password,
  boltUrl,
  name,
  username,
  databaseName,
  onAccept,
  onDecline,
  liveMigration,
}: Props) => {
  const credentialsFilename = `Neo4j-${id}-Created-${new Date().toISOString().slice(0, 10)}.txt`;
  const credentialsURL = useMemo(() => {
    const credentials: InstanceCredentials = {
      id,
      username,
      password,
      boltUrl,
      name,
      databaseName,
    };
    const content: string = formatInstanceCredentials(credentials);
    const blob = new Blob([content], { type: 'text/plain' });
    return window.URL.createObjectURL(blob);
  }, [boltUrl, databaseName, id, name, password, username]);

  return (
    <Dialog
      modalProps={{
        className: 'generated-password-modal',
        'data-testid': 'generated-password-modal',
        'data-instance-id': id,
      }}
      size="small"
      isOpen
      hasDisabledCloseButton
    >
      <Dialog.Header>New credentials for {name}</Dialog.Header>
      <Dialog.Content className="space-y-6">
        <div className="readonly-field">
          <Typography variant="body-medium" className="text-neutral-text-weak">
            Username
          </Typography>
          <p>{username}</p>
        </div>
        <CopyTextInput label="Password" value={password} isPortaled={false} />
        <Banner
          type="warning"
          description="Note that the password will not be available after this point."
          hasIcon
          usage="inline"
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          fill="outlined"
          color="neutral"
          onClick={onDecline}
          htmlAttributes={{
            'data-testid': 'close-password-modal',
          }}
        >
          Close
        </Button>
        <Button
          as="a"
          onClick={onAccept}
          htmlAttributes={{
            href: credentialsURL,
            download: credentialsFilename,
            'data-testid': 'download-and-continue',
          }}
        >
          Download and continue
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
